import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, MailWarning, Send, X } from "lucide-react";

import {
  useCancelOutbound,
  useEmailStatus,
  useOutboundMessages,
  useRetryOutbound,
} from "@/api/queries";
import type { OutboundMessage, OutboundMessageStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDateTime, formatOutboundStatus, formatSendError } from "@/lib/format";
import { outboundTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const STATUSES: OutboundMessageStatus[] = [
  "QUEUED",
  "SENDING",
  "SENT",
  "FAILED",
  "UNKNOWN_DELIVERY",
  "CANCELLED",
];

/**
 * Ce qui est sorti, ou a essayé de sortir (V1.6-05).
 *
 * Cet écran ne déclenche aucun envoi : on y réessaie un échec net et on y
 * annule ce qui n'est pas encore parti. Un message dont la livraison est
 * incertaine ne propose rien — il dit d'aller vérifier, parce que renvoyer
 * ferait peut-être lire deux fois la même demande au fournisseur.
 */
export function OutgoingMessagesPage() {
  const [params, setParams] = useSearchParams();
  const [reading, setReading] = useState<OutboundMessage | null>(null);
  const { data: email } = useEmailStatus();

  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const query = useMemo(
    () => ({
      status: status as OutboundMessageStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, offset],
  );

  const { data, isLoading, error, refetch } = useOutboundMessages(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Messages sortants"
        subtitle="Ce qui a été transmis, ou tenté. Rien ne part d'ici sans qu'on l'ait demandé."
        actions={
          email ? (
            <StatusBadge tone={email.enabled ? "info" : "unknown"}>
              {email.enabled ? `E-mail : ${email.provider}` : "E-mail : non configuré"}
            </StatusBadge>
          ) : null
        }
      />

      {email && !email.enabled && (
        <p className="rounded-lg border border-border bg-surface-raised p-3 text-[13px] text-ink-muted">
          Aucun service d'envoi n'est configuré sur cette installation. Les demandes
          transmises depuis HD-Vault sont inscrites ici, et échouent avec « envoi non
          configuré » : rien n'est parti.
        </p>
      )}

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-64"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatOutboundStatus(value)}
              </option>
            ))}
          </Select>
          {status && (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                status ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <Send className="size-6" aria-hidden />
                )
              }
              title={status ? "Aucun message ne correspond" : "Aucun message sortant"}
              description={
                status
                  ? "Essayez un autre statut."
                  : "Les demandes de prix transmises apparaîtront ici."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="whitespace-nowrap">Date</Th>
                      <Th className="whitespace-nowrap">Demande</Th>
                      <Th className="max-w-0">Destinataire</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Essais
                      </Th>
                      <Th>Statut</Th>
                      <Th className="w-40" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <MessageRow key={item.id} item={item} onRead={setReading} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <MessageCard item={item} onRead={setReading} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <MessageDialog message={reading} onClose={() => setReading(null)} />
    </Page>
  );
}

/**
 * Les deux gestes possibles, et celui qui n'existe pas.
 *
 * Réessayer n'est proposé que sur un échec net. Sur une livraison incertaine
 * l'écran affiche une phrase et aucun bouton : c'est le seul cas où ne rien
 * offrir est la bonne interface.
 */
function Actions({ item }: { item: OutboundMessage }) {
  const toast = useToast();
  const retry = useRetryOutbound();
  const cancel = useCancelOutbound();

  if (item.status === "UNKNOWN_DELIVERY") {
    return (
      <span className="flex items-center gap-1.5 text-[12px] text-warning">
        <MailWarning className="size-3.5 shrink-0" aria-hidden />
        Vérifier avant de renvoyer
      </span>
    );
  }

  return (
    <div className="flex gap-2">
      {item.can_retry && (
        <Button
          variant="secondary"
          size="sm"
          disabled={retry.isPending}
          onClick={() =>
            void retry
              .mutateAsync({ messageId: item.id })
              .catch((failure: unknown) => toast.error("Non réessayé", String(failure)))
          }
        >
          Réessayer
        </Button>
      )}
      {item.can_cancel && (
        <Button
          variant="secondary"
          size="sm"
          disabled={cancel.isPending}
          onClick={() =>
            void cancel
              .mutateAsync({ messageId: item.id })
              .catch((failure: unknown) => toast.error("Non annulé", String(failure)))
          }
        >
          Annuler
        </Button>
      )}
    </div>
  );
}

function MessageRow({
  item,
  onRead,
}: {
  item: OutboundMessage;
  onRead: (message: OutboundMessage) => void;
}) {
  return (
    <Tr>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatDateTime(item.queued_at)}
      </Td>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        {item.rfq_id ? (
          <Link to={`/rfqs/${item.rfq_id}`} className="hover:text-brand">
            {item.rfq_code ?? "—"}
          </Link>
        ) : (
          "—"
        )}
      </Td>
      <Td className="max-w-0">
        <button
          type="button"
          onClick={() => onRead(item)}
          className="block min-w-0 text-left hover:text-brand"
        >
          <span className="block truncate text-ink">{item.to_email}</span>
          <span className="block truncate text-[11px] text-ink-subtle">{item.subject}</span>
        </button>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {item.attempts}
      </Td>
      <Td>
        <StatusBadge tone={outboundTone(item.status)}>
          {formatOutboundStatus(item.status)}
        </StatusBadge>
        {item.last_error && (
          <span className="mt-0.5 block text-[11px] text-ink-subtle">
            {formatSendError(item.last_error)}
          </span>
        )}
      </Td>
      <Td>
        <Actions item={item} />
      </Td>
    </Tr>
  );
}

function MessageCard({
  item,
  onRead,
}: {
  item: OutboundMessage;
  onRead: (message: OutboundMessage) => void;
}) {
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <button type="button" onClick={() => onRead(item)} className="min-w-0 text-left">
        <p className="truncate text-[14px] font-medium text-ink">{item.to_email}</p>
        <p className="truncate text-[12px] text-ink-muted">{item.subject}</p>
        <p className="mt-0.5 text-[11px] text-ink-subtle">
          {formatDateTime(item.queued_at)} · essai {item.attempts}
        </p>
      </button>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={outboundTone(item.status)}>
          {formatOutboundStatus(item.status)}
        </StatusBadge>
        <Actions item={item} />
      </div>
    </div>
  );
}

/** Le message exact qui est parti, ou qui aurait dû. En lecture seule. */
function MessageDialog({
  message,
  onClose,
}: {
  message: OutboundMessage | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={message !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={message?.subject ?? ""}
      description={message ? `À ${message.to_email}` : ""}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {message && (
        <div className="flex flex-col gap-2 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={outboundTone(message.status)}>
              {formatOutboundStatus(message.status)}
            </StatusBadge>
            <span className="text-ink-muted">
              demandé par {message.queued_by} · {formatDateTime(message.queued_at)}
            </span>
          </div>
          {message.sent_at && (
            <p className="text-ink-muted">Transmis le {formatDateTime(message.sent_at)}</p>
          )}
          {message.last_error && (
            <p className="text-ink-muted">{formatSendError(message.last_error)}</p>
          )}
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface-raised p-3 text-[12px] text-ink-muted">
            {message.body_text}
          </pre>
          {message.content_hash && (
            <p className="font-mono text-[11px] text-ink-subtle">
              Empreinte {message.content_hash.slice(0, 16)}…
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
